import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ConfirmPendingExpenseInput,
  CreateExpenseCategoryInput,
  CreateRecurringExpenseInput,
  Expense,
  ExpenseCategory,
  ExpenseCategoryList,
  ExpensePage,
  PendingExpenses,
  Pnl,
  PnlRevenueEntries,
  RecordExpenseInput,
  RecurringExpense,
  RecurringExpenseList,
  UpdateExpenseCategoryInput,
  UpdateExpenseInput,
  UpdateRecurringExpenseInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  confirmPendingExpense,
  createExpenseCategory,
  createRecurringExpense,
  deleteExpense,
  deleteRecurringExpense,
  dismissPendingExpense,
  getExpenseCategories,
  getExpenses,
  getPendingExpenses,
  getPnl,
  getPnlRevenueEntries,
  getRecurringExpenses,
  recordExpense,
  updateExpense,
  updateExpenseCategory,
  updateRecurringExpense,
  type ExpenseListParams,
  type PnlParams,
} from './expenses-api.js';

/**
 * The expense and profit hooks.
 *
 * None of these goes through the offline write queue, and that is the opposite of the call task
 * 17 made for payments — deliberately. A payment is taken at a counter with a customer standing
 * there and must survive a dead connection; an expense is entered sitting down, from a bill that
 * is not going anywhere. Replaying one silently twenty minutes later would risk a duplicate rent
 * in the profit figure, which is a worse failure than "try again when you are back online".
 *
 * Every write invalidates the whole module, because almost all of them move a figure on the P&L:
 * confirming a pending row, deleting a mis-keyed expense and renaming a category all change what
 * the statement says.
 */

export const expenseKeys = {
  all: ['expenses'] as const,
  list: (params: ExpenseListParams) => ['expenses', 'list', params] as const,
  categories: () => ['expenses', 'categories'] as const,
  recurring: () => ['expenses', 'recurring'] as const,
  pending: () => ['expenses', 'pending'] as const,
  pnl: (params: PnlParams) => ['expenses', 'pnl', params] as const,
  pnlRevenue: (params: PnlParams) => ['expenses', 'pnl', 'revenue', params] as const,
};

function useInvalidateExpenses(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
  };
}

export function useExpenses(
  params: ExpenseListParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<ExpensePage> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.list(params),
    queryFn: () => getExpenses(client, params),
    placeholderData: (previous) => previous,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export function useExpenseCategories(): UseQueryResult<ExpenseCategoryList> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.categories(),
    queryFn: () => getExpenseCategories(client),
  });
}

export function useRecurringExpenses(): UseQueryResult<RecurringExpenseList> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.recurring(),
    queryFn: () => getRecurringExpenses(client),
  });
}

export function usePendingExpenses(): UseQueryResult<PendingExpenses> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.pending(),
    queryFn: () => getPendingExpenses(client),
  });
}

export function useRecordExpense(): UseMutationResult<Expense, unknown, RecordExpenseInput> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (input: RecordExpenseInput) => recordExpense(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateExpense(): UseMutationResult<
  Expense,
  unknown,
  { expenseId: string; input: UpdateExpenseInput }
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ expenseId, input }: { expenseId: string; input: UpdateExpenseInput }) =>
      updateExpense(client, expenseId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteExpense(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (expenseId: string) => deleteExpense(client, expenseId),
    onSuccess: invalidate,
  });
}

export function useCreateExpenseCategory(): UseMutationResult<
  ExpenseCategory,
  unknown,
  CreateExpenseCategoryInput
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (input: CreateExpenseCategoryInput) => createExpenseCategory(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateExpenseCategory(): UseMutationResult<
  ExpenseCategory,
  unknown,
  { categoryId: string; input: UpdateExpenseCategoryInput }
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({
      categoryId,
      input,
    }: {
      categoryId: string;
      input: UpdateExpenseCategoryInput;
    }) => updateExpenseCategory(client, categoryId, input),
    onSuccess: invalidate,
  });
}

export function useCreateRecurringExpense(): UseMutationResult<
  RecurringExpense,
  unknown,
  CreateRecurringExpenseInput
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (input: CreateRecurringExpenseInput) => createRecurringExpense(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateRecurringExpense(): UseMutationResult<
  RecurringExpense,
  unknown,
  { recurringId: string; input: UpdateRecurringExpenseInput }
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({
      recurringId,
      input,
    }: {
      recurringId: string;
      input: UpdateRecurringExpenseInput;
    }) => updateRecurringExpense(client, recurringId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteRecurringExpense(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (recurringId: string) => deleteRecurringExpense(client, recurringId),
    onSuccess: invalidate,
  });
}

/** The owner saying yes. Only at this point does a generated row become money. */
export function useConfirmPendingExpense(): UseMutationResult<
  Expense,
  unknown,
  { expenseId: string; input?: ConfirmPendingExpenseInput }
> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ expenseId, input }: { expenseId: string; input?: ConfirmPendingExpenseInput }) =>
      confirmPendingExpense(client, expenseId, input ?? {}),
    onSuccess: invalidate,
  });
}

export function useDismissPendingExpense(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (expenseId: string) => dismissPendingExpense(client, expenseId),
    onSuccess: invalidate,
  });
}

/**
 * The statement. `enabled` is not a nicety: the P&L is the owner's alone, and a screen that
 * fired the query before checking who is looking would put a 403 in every manager's console and
 * a spinner on a page that is about to say "owner only".
 */
export function usePnl(
  params: PnlParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<Pnl> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.pnl(params),
    queryFn: () => getPnl(client, params),
    placeholderData: (previous) => previous,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/** The revenue drill-down. Fetched only once somebody taps the figure. */
export function usePnlRevenueEntries(
  params: PnlParams,
  options: { enabled: boolean },
): UseQueryResult<PnlRevenueEntries> {
  const client = useApiClient();
  return useQuery({
    queryKey: expenseKeys.pnlRevenue(params),
    queryFn: () => getPnlRevenueEntries(client, params),
    enabled: options.enabled,
  });
}
